import React, {forwardRef, useImperativeHandle, useState} from "react";

import {Button, message, Space} from "antd";
import {IClusterPlanLongItem} from "../../../api/apiTypes";
import {useTranslation} from "react-i18next";
import {MergePlanPopup, SearchParams} from "./MergePlanPopup";
import {ShiftPlanPopup} from "./ShiftPlanPopup";
import {IShippingPlanMarker} from "./PlanMap";
import {CreatetPlanPopup} from "./CreatetPlanPopup";
import {showMessage, showMessageWithTitle} from "../../../common/message";
import {isUpdateDisabled} from "../../../common/fns";

interface Props {
  onShifted: () => void;
  onMerged: () => void;
  params: SearchParams;
  planNo?: string;
  planDate?: string;
}

export const PlanControlButtons = forwardRef(({
                                                onShifted,
                                                onMerged,
                                                planNo,
                                                planDate,
                                                params: {sector_code__in, ...params}
                                              }: Props, ref) => {
  const {t} = useTranslation("shipping");
  const [messageApi, messageHolder] = message.useMessage();
  const [selectedPlans, setSelectedPlans] = useState<IClusterPlanLongItem[]>();
  const [selectedTrackings, setSelectedTrackings] = useState<string[]>();
  const [isVisibleMerging, setMergingVisibility] = useState(false);
  const [isVisibleShifting, setShiftingVisibility] = useState(false);
  const [isVisibleCreation, setCreationVisibility] = useState(false);
  
  useImperativeHandle(ref, () => ({
    setPlans: setSelectedPlans, setShippings: createTrackingList,
  }), []);
  
  function createTrackingList(selectedShippings: IShippingPlanMarker[]) {
    const trackingList: string[] = [];
    selectedShippings && selectedShippings.forEach(({shipping_item__tracking_number}) => {
      trackingList.push(shipping_item__tracking_number);
    });
    setSelectedTrackings(trackingList);
  }
  
  const handleCreatePlan = () => {
    if (!planNo) {
      showMessage(messageApi, t("Please, select plan no first"), "warning");
      return;
    }
    if (!!selectedTrackings && selectedTrackings.length > 0) {
      setCreationVisibility(true);
      return
    }
    showMessage(messageApi, t("popup.error.empty"), "warning");
  };
  
  const containUpdateDisabledShippings = (plan: IClusterPlanLongItem) => {
    console.log("containUpdateDisabledShippings", plan.count_scanned_all)
    return (!plan.shippingitems_geo || plan.shippingitems_geo.filter((geo) => isUpdateDisabled(geo.shipping_item__status, geo.is_scanned)).length);
  }
  
  const handleMergePlan = () => {
    if (!!selectedPlans && selectedPlans.length > 0) {
      if (selectedPlans.length === 1) {
        if (containUpdateDisabledShippings(selectedPlans[0])) {
          showMessage(messageApi, t("Please, select a plan not sorted"), "warning");
        }
        else {
          setMergingVisibility(true);
        }
      }
      else {
        showMessage(messageApi, t("Please, select only one plan to merge"), "warning");
      }
    }
    else {
      showMessage(messageApi, t("Please, select plan"), "warning");
    }
  };
  
  const handleShiftShipping = () => {
    if (!!selectedTrackings && selectedTrackings.length > 0) {
      setShiftingVisibility(true);
    }
    else {
      showMessage(messageApi, t("popup.error.empty"), "warning");
    }
  };
  
  const handleMerged = () => {
    setMergingVisibility(false);
    showMessageWithTitle(t("Plans were merged successfully"));
    onMerged();
  }
  
  const handleShifted = () => {
    setShiftingVisibility(false);
    showMessageWithTitle(t("Shippings were shifted successfully"));
    onShifted();
  }
  
  const handleCreated = () => {
    setCreationVisibility(false);
    showMessageWithTitle(t("Plan was created successfully"));
    onMerged();
  }
  
  return (<Space size={1}>
    {messageHolder}
    <Button
      type={"primary"}
      style={{height: 32, borderRadius: 2}}
      onClick={handleCreatePlan}
    >
      {t("create plan")}
    </Button>
    <Button
      type={"primary"}
      size={"small"}
      style={{height: 32, borderRadius: 2}}
      onClick={handleMergePlan}
    >
      {t("merge plan")}
    </Button>
    <Button
      type={"primary"}
      size={"small"}
      style={{height: 32, borderRadius: 2}}
      onClick={handleShiftShipping}
    >
      {t("shift shipping")}
    </Button>
    {isVisibleMerging && selectedPlans && selectedPlans?.length > 0 && (<MergePlanPopup
      plan={selectedPlans[0]}
      params={params}
      onMerged={handleMerged}
      onHide={() => setMergingVisibility(false)}
    />)}
    {isVisibleShifting && selectedTrackings && selectedTrackings?.length > 0 && (<ShiftPlanPopup
      trackingNumbers={selectedTrackings}
      params={params}
      onShifted={handleShifted}
      onHide={() => setShiftingVisibility(false)}
    />)}
    {isVisibleCreation && selectedTrackings && selectedTrackings?.length > 0 && planNo && (<CreatetPlanPopup
      trackingNumbers={selectedTrackings}
      planNo={planNo}
      planDate={planDate}
      onCreated={handleCreated}
      onHide={() => setCreationVisibility(false)}
    />)}
  </Space>);
});
